import type { AccordionItem } from '@/app/components/atoms/Accordion'

import styles from '../FaqAccordion.module.scss'
import type { SanitizedFaqEntry } from '../FaqAccordion.types'

/**
 * Versión en texto plano y sin puntuación de una pregunta HTML saneada, para
 * `AccordionItem.analyticsLabel` (`component_label` del `ui_interaction` de
 * `Accordion` — `Entradas + FAQs.csv`). Las preguntas reales de este sitio son
 * texto plano casi siempre; esta función solo cubre el caso con formato
 * (`<strong>`, enlaces) sin volver a depender de DOMPurify para un simple
 * texto de analítica. Descarta además signos como `¿`/`?` — `slugify` (guía
 * §3) solo pasa a minúsculas, quita tildes y cambia espacios por `_`; no
 * elimina puntuación, así que debe llegarle ya limpia.
 */
function stripHtml(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Mapea entradas ya saneadas a los paneles del átomo `Accordion`, aplicando la
 * tipografía de pregunta/respuesta de `FaqAccordion.module.scss`.
 *
 * El HTML que se inserta aquí **ya viene saneado** por `sanitizeFaqItems`
 * (DOMPurify, en servidor); esta función no vuelve a sanear ni acepta HTML
 * crudo — de ahí que su entrada sea `SanitizedFaqEntry` y no la entrada del
 * consumidor.
 *
 * Compartida por las dos maquetaciones del acordeón —listado único
 * (`FaqAccordion`) y listado filtrado por grupo (`FaqAccordionGroups`)— para
 * que pregunta y respuesta se pinten igual en ambas.
 */
export function toAccordionItems(entries: readonly SanitizedFaqEntry[]): AccordionItem[] {
  return entries.map((entry) => ({
    id: entry.id,
    anchorId: entry.anchorId,
    title: (
      <span
        className={styles.question}
        dangerouslySetInnerHTML={{ __html: entry.questionHtml }}
      />
    ),
    content: (
      <div className={styles.answer} dangerouslySetInnerHTML={{ __html: entry.answerHtml }} />
    ),
    analyticsLabel: stripHtml(entry.questionHtml),
  }))
}
